import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  getDevice,
  getDevices,
  getDue,
  getHouseFiles,
  getHousePage,
  getHousePages,
} from './api';
import type {
  DevicesResponse,
  DueItem,
  HouseDevice,
  HouseFile,
  HousePage,
  HousePagesResponse,
  HouseSection,
} from './api';
import { todayISO } from './dates';

// Data hooks for the house sections. Each read paints from the last
// copy this tab saw (in memory only) and refetches on mount, so going
// back and forth between a page and its section is instant.

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  /** 403: signed in, but not a member of the household that owns the house data. */
  forbidden: boolean;
  /** 404 from the Worker (an unknown page). */
  notFound: boolean;
  loading: boolean;
  reload: () => void;
}

const memory = new Map<string, unknown>();

function useCachedLoad<T>(key: string | null, load: () => Promise<T>): Loaded<T> {
  const [data, setData] = useState<T | null>(() => (key ? ((memory.get(key) as T) ?? null) : null));
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(0);
  const [loading, setLoading] = useState(key !== null);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    setData((memory.get(key) as T) ?? null);
    setLoading(true);
    loadRef
      .current()
      .then((r) => {
        if (cancelled) return;
        memory.set(key, r);
        setData(r);
        setError(null);
        setStatus(0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load.');
        setStatus(err instanceof ApiError ? err.status : 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, forbidden: status === 403, notFound: status === 404, loading, reload };
}

export function usePublishedPages(): Loaded<HousePagesResponse> & { paths: ReadonlySet<string> | null } {
  const res = useCachedLoad('pages', getHousePages);
  const paths = useMemo(
    () => (res.data ? new Set(res.data.pages.map((p) => p.path)) : null),
    [res.data],
  );
  return { ...res, paths };
}

export function useHousePage(path: string | null): Loaded<HousePage> {
  return useCachedLoad(path ? `page:${path}` : null, () => getHousePage(path!));
}

export function useHouseFiles(section: HouseSection): Loaded<HouseFile[]> {
  return useCachedLoad(`files:${section}`, () => getHouseFiles(section).then((r) => r.files));
}

/** Every due item, statuses computed for the browser's local today. */
export function useDue(): Loaded<DueItem[]> {
  return useCachedLoad('due', () => getDue({ within: 30, today: todayISO() }).then((r) => r.items));
}

/** The Portland device registry with each device's manuals and due items
 *  (statuses for the browser's local today, due-soon within 30 days, as
 *  useDue). */
export function useDevices(): Loaded<DevicesResponse> {
  return useCachedLoad('devices', () => getDevices({ within: 30, today: todayISO() }));
}

/** One device; paints from the device list when this tab has it. */
export function useDevice(id: string | null): Loaded<HouseDevice> {
  const res = useCachedLoad(id ? `device:${id}` : null, () => getDevice(id!, { within: 30, today: todayISO() }));
  const listed = (memory.get('devices') as DevicesResponse | undefined)?.devices.find((d) => d.id === id) ?? null;
  return res.data || !listed ? res : { ...res, data: listed };
}

/** Overdue and due within 30 days, all areas; empty on any error (Today
 *  shows the card only when there's something to show). */
export function useDueSoon(): { items: DueItem[]; reload: () => void } {
  const due = useDue();
  const items = useMemo(
    () => (due.data ?? []).filter((i) => i.status === 'overdue' || i.status === 'due-soon'),
    [due.data],
  );
  return { items, reload: due.reload };
}
